import {Firestore} from "@/plugins/Firestore";
import {OpenAICompletionData, OpenAiCompletionType} from "@/model/OpenAI";
import {Unsubscribe} from "firebase/firestore";
import {Store} from "@/plugins/Store";
import { Article, ArticleContent, ArticleContentKey, ArticlePartContent, KeywordForAi, normalizeBlockProposals } from "@/model/Article";
import {reformatOpenAiResponseToHtml} from "@/utils/string";
import {KeywordType} from "@/enum/keyword_type";
import { findLastNonEmptyIndex } from "@/utils/Article";
import {clearArticleAiLoadingFlags} from "@/service/aiWaitFallback";
import {textsMatch} from "@/service/briefEditorActions";
import {ensureNaturalHeading, collapseDuplicateHeadingLead, headingForUrlImproveSlot, isGrammaticalQuestion, isManufacturedOutlineHeading, type HeadingRole} from "@/service/articleSanitize";
import {slotHasWrittenText} from "@/utils/articleAiLock";
import {resolveArticleMainKeyword} from '@/service/articleMainKeyword';
import {
    collectMovableHeadings,
    finalizeAnalyzedBody,
    findHeadingIndex,
    headingHasBody,
    insertBeforeDisclaimer,
    isEmptyHeadingSlot,
    isOffPageCompetitorTopic,
    pageCorpus,
    planAnalyzeHeadingWrite,
    settleSectionOrganizations,
    shouldDropTitledEmptyDuplicate,
    placeholderSlotsToAppend,
    plainHeadingText,
} from '@/service/analyzeFidelity';
import {selectWritableCompletions, shouldWaitForWholeHeadingCount} from "@/service/wholeArticleApply";
import {settleProcessingParts} from "@/service/partsTerminalApply";
import {hollowReviewFailure} from "@/service/blockProposals";
import {PARTS_GENERATE_DEADLINE_MS, partsRunMaySettle} from "@/service/partsTerminalState";
import {EventBus} from "@/utils/EventBus";
import {getUid} from "@/utils/uid";

let serverArticleCommitted = false;

function clientMayApplyCompletion(): boolean {
    if (serverArticleCommitted) {
        return false;
    }
    const article = Store.getters.article;
    return article?.enhanced !== 'done' && article?.aiGenerateStatus !== 'done';
}

function adoptCommittedArticle(incoming: Article): void {
    serverArticleCommitted = true;
    const article = Store.getters.article;
    if (!article) {
        return;
    }
    for (const part of ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const) {
        const blocks = incoming[part];
        if (!Array.isArray(blocks)) {
            continue;
        }
        Store.commit('setArticleContentArray', {
            contentPart: part,
            newArray: blocks.map((block) => ({
                h: block?.h || '',
                p: block?.p || '',
                uid: block?.uid || getUid(),
            })),
        });
    }
    if (Array.isArray(incoming.questions)) {
        Store.commit('replaceArticleQuestions', incoming.questions.map((item) => ({
            question: item?.question || '',
            answer: item?.answer || '',
        })));
    }
    if (typeof incoming.metaTitle === 'string') {
        Store.commit('setArticleMeta', {metaPart: 'metaTitle', value: incoming.metaTitle});
    }
    if (typeof incoming.metaDescription === 'string') {
        Store.commit('setArticleMeta', {metaPart: 'metaDescription', value: incoming.metaDescription});
    }
    if (incoming.enhanced) {
        article.enhanced = incoming.enhanced;
    }
    if (incoming.aiGenerateStatus) {
        article.aiGenerateStatus = incoming.aiGenerateStatus;
    }
}

const watchers: Record<string, Unsubscribe> = {};
const headingAnchors: Partial<Record<OpenAiCompletionType, number>> = {};
const pendingCompletionParts = new Set<string>();
let wholeUnlockTimer: ReturnType<typeof setTimeout> | null = null;
let wholeStatusHandled = false;
const WHOLE_GENERATE_UNLOCK_MS = 8 * 60 * 1000;
const PARTS_GENERATE_UNLOCK_MS = PARTS_GENERATE_DEADLINE_MS;

/**
 * Polish-as-step-2 is gone. Whole-article generate writes the article, then
 * splits it to H headings — never enqueue a second rewrite after that.
 * URL-analyze uses enhance ("Vylepšit článek") as the first generation, not polish.
 */
export function shouldSkipAutoPolish(_article?: Article | null): boolean {
    return true;
}

function isWholeGenerate(data?: OpenAICompletionData | null): boolean {
    return data?.mode === 'whole'
        || Store.getters.article?.aiGenerateMode === 'whole';
}

function unlockAllRemainingSlots(failed = false): void {
    pendingCompletionParts.clear();
    if (failed) {
        Store.commit('setArticleAiFailed', true);
    }
    Store.commit('setArticlePolish', 'skipped');
    Store.commit('setArticleAiPolishing', false);
    clearArticleAiLoadingFlags();
}

function markPartPending(part: string): void {
    pendingCompletionParts.add(part);
}

function markPartSettled(part: string): void {
    pendingCompletionParts.delete(part);
    unlockFilledAndOrphanSlots();
    if (pendingCompletionParts.size === 0) {
        const loading = Store.getters.articleAiLoading;
        if (loading && loading.loadingRatioTotal > 0) {
            unlockAllRemainingSlots(false);
        } else {
            Store.commit('setArticleSeoScoreAiLoadingCompleted', true);
            Store.commit('setArticleSeoScoreChecked', true);
            Store.commit('setArticleAiPolishing', false);
            Store.commit('setArticleAiEnhancing', false);
        }
    }
    const article = Store.getters.article;
    if (partsRunMaySettle(article)) {
        void settleProcessingParts(article).then((decision) => {
            if (decision.action === 'failed') {
                unlockAllRemainingSlots(true);
                Store.commit('setArticleAiEnhancing', false);
            } else if (decision.action === 'success') {
                EventBus.$emit('generation-applied');
            }
        });
    } else if (!Store.getters.articleAiFailed && pendingCompletionParts.size === 0 && isWholeGenerate()) {
        EventBus.$emit('generation-applied');
    }
}

export function unlockFilledAndOrphanSlots(): void {
    const article: Article = Store.getters.article;
    const loading = Store.getters.articleAiLoading;
    if (!article || !loading) {
        return;
    }
    let changed = false;
    for (let level = 1; level <= 6; level++) {
        const part = ('h' + level) as ArticleContentKey;
        const blocks = (article as any)[part] || [];
        const loaders = (loading as any)[part] || [];
        const max = Math.max(blocks.length, loaders.length);
        for (let index = 0; index < max; index++) {
            const loader = loaders[index];
            if (!loader || (!loader.h && !loader.p)) {
                continue;
            }
            const block = blocks[index] as ArticlePartContent | undefined;
            if (index >= blocks.length || (block && slotHasWrittenText(block.h, block.p))) {
                setContentLoading(part, index, false);
                changed = true;
            }
        }
    }
    const questions = article.questions || [];
    const questionLoaders = loading.questions || [];
    const questionMax = Math.max(questions.length, questionLoaders.length);
    for (let index = 0; index < questionMax; index++) {
        const loader = questionLoaders[index];
        if (!loader || (!loader.question && !loader.answer)) {
            continue;
        }
        const question = questions[index];
        if (index >= questions.length || (question && slotHasWrittenText(question.question, question.answer))) {
            setQuestionLoading(index, false);
            changed = true;
        }
    }
    if (loading.metaTitle && slotHasWrittenText(article.metaTitle)) {
        Store.commit('setArticleMetaAiLoading', {metaPart: 'metaTitle', value: false});
        changed = true;
    }
    if (loading.metaDescription && slotHasWrittenText(article.metaDescription)) {
        Store.commit('setArticleMetaAiLoading', {metaPart: 'metaDescription', value: false});
        changed = true;
    }
    let extras = false;
    for (let level = 1; level <= 6; level++) {
        const part = ('h' + level) as ArticleContentKey;
        const blocks = (article as any)[part] || [];
        const loaders = (loading as any)[part] || [];
        if (loaders.length > blocks.length) {
            extras = true;
            break;
        }
    }
    if (!extras && (loading.questions || []).length > (article.questions || []).length) {
        extras = true;
    }
    if (extras) {
        Store.commit('trimArticleAiLoading');
        changed = true;
    }
    if (changed) {
        loading.computeLoadingRatio();
    }
}

function unlockHeadingPart(aiPart: OpenAiCompletionType & ArticleContentKey): void {
    const article: Article = Store.getters.article;
    const loading = Store.getters.articleAiLoading;
    const slots = (article[aiPart] || []) as ArticlePartContent[];
    const loaders = (loading && (loading as any)[aiPart]) || [];
    const max = Math.max(slots.length, loaders.length, 1);
    for (let index = 0; index < max; index++) {
        setContentLoading(aiPart, index, false);
    }
}

function clearWholeUnlockTimer(): void {
    if (wholeUnlockTimer !== null) {
        clearTimeout(wholeUnlockTimer);
        wholeUnlockTimer = null;
    }
}

function armAiUnlock(): void {
    if (wholeUnlockTimer !== null) {
        return;
    }
    const startedAt = Number(Store.getters.article?.ai_content_last_generated_at || 0);
    const elapsed = startedAt ? Date.now() - startedAt : 0;
    const budget = isWholeGenerate() ? WHOLE_GENERATE_UNLOCK_MS : PARTS_GENERATE_UNLOCK_MS;
    const wait = Math.max(0, budget - elapsed);
    wholeUnlockTimer = setTimeout(() => {
        wholeUnlockTimer = null;
        const article = Store.getters.article;
        if (partsRunMaySettle(article)) {
            void settleProcessingParts(article).then((decision) => {
                unlockFilledAndOrphanSlots();
                if (decision.action === 'failed') {
                    unlockAllRemainingSlots(true);
                } else if (decision.action === 'success') {
                    EventBus.$emit('generation-applied');
                }
            });
            return;
        }
        unlockFilledAndOrphanSlots();
        const loading = Store.getters.articleAiLoading;
        if (loading && loading.loadingRatioTotal > 0) {
            unlockAllRemainingSlots(true);
        }
    }, wait);
}

export function rearmGenerationStatusWatch(): void {
    watchWholeGenerateStatus();
}

function watchWholeGenerateStatus(): void {
    const article: Article = Store.getters.article;
    if (!article?.id) {
        return;
    }
    // Retry calls watchForAi again. The previous failure must not swallow the next done or failed.
    if (watchers['whole-status']) {
        watchers['whole-status']();
        delete watchers['whole-status'];
    }
    wholeStatusHandled = false;
    clearWholeUnlockTimer();
    armAiUnlock();
    const runStartedAt = Number(article.ai_content_last_generated_at || 0);
    watchers['whole-status'] = Firestore.createUserArticleWatcher(article.id, (newArticle: Article) => {
        if (newArticle.polish === 'skipped' || newArticle.polish === 'failed' || newArticle.polish === 'done') {
            Store.commit('setArticlePolish', newArticle.polish);
            Store.commit('setArticleAiPolishing', false);
        }
        if (wholeStatusHandled) {
            return;
        }
        const incomingStarted = Number(newArticle.ai_content_last_generated_at || 0);
        // A snapshot from the previous run (or a failed doc with no clock) is not this run.
        if (runStartedAt && incomingStarted && incomingStarted < runStartedAt) {
            return;
        }
        if (newArticle.aiGenerateStatus === 'failed' && runStartedAt && incomingStarted < runStartedAt) {
            return;
        }
        if (newArticle.aiGenerateStatus === 'review') {
            const proposals = normalizeBlockProposals((newArticle as {blockProposals?: unknown}).blockProposals);
            const failure = hollowReviewFailure({
                isAnalyze: newArticle.isAnalyze,
                aiGenerateStatus: 'review',
                blockProposals: proposals,
            });
            if (failure) {
                wholeStatusHandled = true;
                const current = Store.getters.article;
                if (current) {
                    current.enhanced = failure.enhanced;
                    current.aiGenerateStatus = failure.aiGenerateStatus;
                    current.blockProposals = [];
                    void Firestore.updateArticleGenerationStatus(current, failure);
                }
                clearWholeUnlockTimer();
                unlockAllRemainingSlots(true);
                EventBus.$emit('parts-generation-failed');
                return;
            }
            wholeStatusHandled = true;
            const current = Store.getters.article;
            if (current) {
                current.enhanced = newArticle.enhanced === 'done' ? 'done' : 'pending';
                current.aiGenerateStatus = 'review';
                current.blockProposals = proposals;
            }
            clearWholeUnlockTimer();
            unlockAllRemainingSlots(false);
            EventBus.$emit('proposals-ready');
            return;
        }
        if (newArticle.aiGenerateStatus === 'done' || newArticle.enhanced === 'done') {
            wholeStatusHandled = true;
            const current = Store.getters.article;
            const runInProgress = current?.enhanced === 'processing' || current?.aiGenerateStatus === 'running';
            clearWholeUnlockTimer();
            unlockAllRemainingSlots(false);
            if (!runInProgress) {
                return;
            }
            adoptCommittedArticle(newArticle);
            EventBus.$emit('generation-applied');
        } else if (newArticle.aiGenerateStatus === 'failed') {
            wholeStatusHandled = true;
            const article = Store.getters.article;
            if (article) {
                if (newArticle.enhanced) {
                    article.enhanced = newArticle.enhanced;
                }
                article.aiGenerateStatus = 'failed';
            }
            clearWholeUnlockTimer();
            unlockAllRemainingSlots(true);
            EventBus.$emit('parts-generation-failed');
        }
    });
}

export function watchForAi(): Promise<void[]> {
    const promises = [];

    if (Store.getters.article?.isAnalyze) {
        watchWholeGenerateStatus();
    }
    if (!Store.getters.article.aiPrompt && !Store.getters.article.noAiPrompt) {
        return Promise.resolve([]);
    }

    Store.commit('setArticleAiFailed', false);
    pendingCompletionParts.clear();
    watchWholeGenerateStatus();
    armAiUnlock();

    const article: Article = Store.getters.article;
    if (!article.metaTitle.length || !article.metaDescription.length) {
        promises.push(Firestore.getArticleOpenAIState(Store.getters.article.id, 'meta')
            .then((response: OpenAICompletionData | null) => {
                if (!response) {
                    setMetaLoading(false);
                    return;
                }
                markPartPending('meta');
                watchForAiMeta();
            }));
    }
    for (const aiPart of <(OpenAiCompletionType & keyof ArticleContent)[]>['h1', 'h2', 'h3', 'h4', 'h5', 'h6']) {
        promises.push(Firestore.getArticleOpenAIState(Store.getters.article.id, aiPart)
            .then((response: OpenAICompletionData | null) => {
                if (response) {
                    if (response.mode === 'whole') {
                        Store.commit('setArticlePolish', 'skipped');
                        Store.commit('setArticleAiPolishing', false);
                        armAiUnlock();
                    }
                    markPartPending(aiPart);
                    syncHeadingSlots(aiPart, response);
                    watchHeadingPart(aiPart);
                }
            }));
    }
    promises.push(Firestore.getArticleOpenAIState(Store.getters.article.id, 'question')
        .then((response: OpenAICompletionData | null) => {
            if (response && article.keywordsForAi) {
                const questionsForAi = article.keywordsForAi.filter((keyword: KeywordForAi) => keyword.type === KeywordType.QUESTION)
                                        .map((keyword: KeywordForAi) => keyword.keyword);

                // Path B stages proposals after Vylepšit. Do not insert FAQ rows before accept.
                for (const [index, articleQuestion] of article.questions.entries()) {
                    // TODO use uid or some better id than 'question' itself
                    if (!articleQuestion.answer.length && questionsForAi.includes(articleQuestion.question)) {
                        watchForAiQuestion(index, articleQuestion.question);
                    }
                }
            }
            if (response) {
                markPartPending('question');
                watchQuestionPart();
            } else {
                const questions = Store.getters.article.questions || [];
                questions.forEach((_: unknown, index: number) => setQuestionLoading(index, false));
            }
        }));

    return Promise.all(promises).then((result) => {
        unlockFilledAndOrphanSlots();
        if (pendingCompletionParts.size === 0) {
            const loading = Store.getters.articleAiLoading;
            if (loading && loading.loadingRatioTotal > 0) {
                unlockAllRemainingSlots(false);
            }
        }
        const startedAt = Number(Store.getters.article?.ai_content_last_generated_at || 0);
        const budget = isWholeGenerate() ? WHOLE_GENERATE_UNLOCK_MS : PARTS_GENERATE_UNLOCK_MS;
        if (startedAt && Date.now() - startedAt >= budget) {
            unlockAllRemainingSlots(true);
        }
        return result;
    });
}

export function watchForAiPolish(): Promise<void> {
    if (!Store.getters.article.aiPrompt && !Store.getters.article.noAiPrompt) {
        return Promise.resolve();
    }

    const article = Store.getters.article;
    if (shouldSkipAutoPolish(article)) {
        Store.commit('setArticlePolish', 'skipped');
        Store.commit('setArticleAiPolishing', false);
        return Promise.resolve();
    }

    watchers[article.id] = Firestore.createUserArticleWatcher(article.id, (newArticle: Article) => {
        for (const contentPart of <(keyof ArticleContent)[]>['h1', 'h2', 'h3', 'metaTitle', 'metaDescription']) {
            // iterate over all content parts - replace content if needed
            if (Array.isArray(newArticle[contentPart])) {
                for (let i = 0; i < newArticle[contentPart].length; ++i) {
                    const newContentPart = newArticle[contentPart][i] as ArticlePartContent;
                    const oldContentPart = article[contentPart][i]  as ArticlePartContent;
                    if (newContentPart.h !== oldContentPart.h) {
                        Store.commit('setArticleContent', {
                            contentPart: contentPart,
                            index: i,
                            contentKey: 'h',
                            value: newContentPart.h
                        });
                    }
                    if (newContentPart.p !== oldContentPart.p) {
                        Store.commit('setArticleContent', {
                            contentPart: contentPart,
                            index: i,
                            contentKey: 'p',
                            value: newContentPart.p
                        });
                    }
                }
                // remove content if new content is shorter
                if (newArticle[contentPart].length < article[contentPart].length) {
                    for (let i = newArticle[contentPart].length; i < article[contentPart].length; ++i) {
                        Store.commit('removeArticleContentSubPart', {
                            contentPart: contentPart,
                            index: i
                        });
                    }
                }
            } else {
                // if not array, it is metaTitle or metaDescription
                if (newArticle[contentPart] !== article[contentPart]) {
                    Store.commit('setArticleMeta', {
                        metaPart: contentPart,
                        value: newArticle[contentPart]
                    });
                }
            }
        }

        if (newArticle.polish === "done" || newArticle.polish === "failed" || newArticle.polish === "skipped") {
            Store.commit('setArticlePolish', newArticle.polish);
            Store.commit('setArticleAiPolishing', false);
            // unsubscribe
            watchers[article.id]();
            delete watchers[article.id];
        }
    });

    return Promise.resolve();
}

function setMetaLoading(value: boolean) {
    for (const metaPart of ['metaTitle', 'metaDescription']) {
        Store.commit('setArticleMetaAiLoading', {
            metaPart: metaPart,
            value: value
        });
    }
}

function setContentLoading(contentPart: ArticleContentKey, index: number, value: boolean) {
    for (const contentKey of ['h', 'p']) {
        Store.commit('setArticleContentAiLoading', {
            contentPart: contentPart,
            index: index,
            contentKey: contentKey,
            value: value
        });
    }
}

function setQuestionLoading(index: number, value: boolean) {
    for (const questionKey of ['question', 'answer']) {
        Store.commit('setArticleQuestionAiLoading', {
            index: index,
            questionKey: questionKey,
            value: value
        });
    }
}

function articleHeadingOpts(role: HeadingRole, keyword?: string) {
    const article: Article = Store.getters.article;
    return {
        mainKeyword: resolveArticleMainKeyword(article || {}) || keyword || '',
        language: article?.language_code || 'cs',
        role,
    };
}

function naturalEditorHeading(heading: unknown, keyword: unknown, role: HeadingRole): string {
    return ensureNaturalHeading(heading, keyword, articleHeadingOpts(role, String(keyword || '')));
}

function savedFaqQuestion(aiQuestion: {value1?: string | null; keyword?: string | null}, existing: unknown): string {
    const saved = plainHeadingText(aiQuestion.keyword || existing);
    const generated = plainHeadingText(aiQuestion.value1 || aiQuestion.keyword);
    if (saved && isGrammaticalQuestion(saved)) {
        return saved;
    }
    if (generated && !isManufacturedOutlineHeading(
        generated,
        aiQuestion.keyword,
        articleHeadingOpts('question', String(aiQuestion.keyword || ''))
    )) {
        return generated;
    }
    return generated || saved;
}

function headingAnchor(aiPart: OpenAiCompletionType & ArticleContentKey): number {
    const article: Article = Store.getters.article;
    return findLastNonEmptyIndex<ArticlePartContent>(article[aiPart] || [], (item) => [item.p, item.h]);
}

function syncHeadingSlots(aiPart: OpenAiCompletionType & ArticleContentKey, data: OpenAICompletionData): number {
    const article: Article = Store.getters.article;
    if (aiPart === 'h1') {
        // Whole-article JSON split used to append a second H1 when h1[0] already
        // had a title. The editor cannot add extra H1s by hand — keep exactly one.
        if (!article.h1 || !article.h1[0]) {
            Store.commit('insertArticleContentSubPart', {
                contentPart: 'h1',
                index: 0,
                aiLoading: !(data.touch > 0) && data.state !== 'failed',
                keepContent: false,
            });
        }
        while ((Store.getters.article.h1 || []).length > 1) {
            Store.commit('removeArticleContentSubPart', {
                contentPart: 'h1',
                index: Store.getters.article.h1.length - 1,
            });
        }
        if (data.count > 0 && data.state !== 'failed' && !(data.touch > 0)) {
            const block = Store.getters.article.h1[0];
            if (block && !slotHasWrittenText(block.h, block.p)) {
                setContentLoading('h1', 0, true);
            }
        }
        return -1;
    }
    if (headingAnchors[aiPart] === undefined) {
        headingAnchors[aiPart] = headingAnchor(aiPart);
    }
    const lastNonEmptyIndex = headingAnchors[aiPart] as number;
    for (let i = 0; i <= lastNonEmptyIndex; i++) {
        if (article[aiPart][i]) {
            // Keep scraped/existing copy; do not splice extra aiLoading rows
            // (that used to leave orphan flags that kept „Obsah článku“ spinning).
            setContentLoading(aiPart, i, false);
        }
    }
    const extras = placeholderSlotsToAppend(!!article.isAnalyze, aiPart, data.count || 0);
    let partIndex = lastNonEmptyIndex + 1;
    const needed = lastNonEmptyIndex + extras + 1;
    while (partIndex < needed) {
        const existing = article[aiPart][partIndex];
        if (!existing) {
            Store.commit('insertArticleContentSubPart', {
                contentPart: aiPart,
                index: partIndex,
                aiLoading: !(data.touch > 0) && data.state !== 'failed',
                keepContent: false
            });
        } else if (
            !(data.touch > 0)
            && data.state !== 'failed'
            && !slotHasWrittenText(existing.h, existing.p)
        ) {
            setContentLoading(aiPart, partIndex, true);
        }
        partIndex++;
    }
    return lastNonEmptyIndex;
}

function dropEmptyHeadingSlots(): void {
    const article = Store.getters.article;
    if (!article?.isAnalyze) {
        return;
    }
    for (const part of ['h2', 'h3', 'h4', 'h5', 'h6'] as ArticleContentKey[]) {
        const blocks = (article[part] || []) as ArticlePartContent[];
        const dropAt: number[] = [];
        blocks.forEach((block, index) => {
            const emptySlot = isEmptyHeadingSlot(block) || (part === 'h2' && shouldDropTitledEmptyDuplicate(blocks, index));
            const emptyLower = part !== 'h2' && !headingHasBody(block?.p);
            if (emptySlot || emptyLower) {
                dropAt.push(index);
            }
        });
        dropAt.sort((left, right) => right - left).forEach((index) => {
            Store.commit('removeArticleContentSubPart', {
                contentPart: part,
                index,
            });
        });
    }
}

function applyAnalyzeHeadingPart(aiPart: OpenAiCompletionType & ArticleContentKey): Promise<void> {
    if (Store.getters.article?.isAnalyze) {
        return Promise.resolve();
    }
    if (!clientMayApplyCompletion()) {
        return Promise.resolve();
    }
    return Firestore.getArticleOpenAIValues(Store.getters.article.id, aiPart)
        .then((content) => {
            const values = selectWritableCompletions(content, aiPart);
            const article = Store.getters.article;
            const corpus = pageCorpus(article as unknown as Record<string, unknown>);
            values.forEach((value) => {
                if (!clientMayApplyCompletion()) {
                    return;
                }
                const written = plainHeadingText(value?.value1);
                if (!value || !written) {
                    return;
                }
                const keyword = plainHeadingText(value.keyword || written);
                if (!keyword || isOffPageCompetitorTopic(keyword, corpus)) {
                    return;
                }
                const blocks = (article[aiPart] || []) as ArticlePartContent[];
                const existingIndex = findHeadingIndex(blocks, written);
                const heading = existingIndex >= 0
                    ? plainHeadingText(blocks[existingIndex].h) || written
                    : headingForUrlImproveSlot(written, keyword, articleHeadingOpts(aiPart as HeadingRole, keyword));
                const previousBody = existingIndex >= 0 ? blocks[existingIndex].p : '';
                const movable = collectMovableHeadings(article as unknown as Record<string, unknown>);
                const settled = settleSectionOrganizations(previousBody, value.value2 || '', corpus);
                const body = finalizeAnalyzedBody(
                    settled,
                    corpus,
                    previousBody,
                    movable,
                    heading
                );
                const plan = planAnalyzeHeadingWrite(blocks, heading, body);
                if (plan.action === 'skip') {
                    return;
                }
                let index = plan.action === 'update' ? plan.index : -1;
                if (index < 0) {
                    index = insertBeforeDisclaimer(article[aiPart] || []);
                    Store.commit('insertArticleContentSubPart', {
                        contentPart: aiPart,
                        index,
                        aiLoading: false,
                        keepContent: false,
                    });
                }
                Store.commit('setArticleContent', {
                    contentPart: aiPart,
                    index,
                    contentKey: 'h',
                    value: plan.heading,
                });
                Store.commit('setArticleContent', {
                    contentPart: aiPart,
                    index,
                    contentKey: 'p',
                    value: body,
                });
            });
            dropEmptyHeadingSlots();
        });
}

function watchHeadingPart(aiPart: OpenAiCompletionType & ArticleContentKey): void {
    if (watchers[aiPart]) {
        return;
    }
    watchers[aiPart] = Firestore.createArticleOpenAIWatcher(Store.getters.article.id, aiPart, (data: OpenAICompletionData) => {
        if (Store.getters.article?.isAnalyze && aiPart !== 'h1') {
            if (data.state === 'failed') {
                unlockHeadingPart(aiPart);
                markPartSettled(aiPart);
                return;
            }
            if (data.count === 0) {
                unlockHeadingPart(aiPart);
                markPartSettled(aiPart);
                return;
            }
            if (data.touch > 0) {
                applyAnalyzeHeadingPart(aiPart)
                    .catch(() => null)
                    .then(() => {
                        unlockHeadingPart(aiPart);
                        unlockFilledAndOrphanSlots();
                        markPartSettled(aiPart);
                    });
            }
            return;
        }
        const lastNonEmptyIndex = syncHeadingSlots(aiPart, data);
        if (data.state === 'failed') {
            unlockHeadingPart(aiPart);
            if (isWholeGenerate(data)) {
                unlockAllRemainingSlots(true);
            } else {
                markPartSettled(aiPart);
            }
            return;
        }
        if (data.count === 0) {
            if (shouldWaitForWholeHeadingCount(data.mode, data.count, aiPart) || (
                isWholeGenerate(data) && (aiPart === 'h1' || aiPart === 'h2')
            )) {
                return;
            }
            unlockHeadingPart(aiPart);
            markPartSettled(aiPart);
            return;
        }
        if (data.touch > 0) {
            const jobs: Promise<unknown>[] = [];
            const writeCount = aiPart === 'h1' ? Math.min(1, data.count || 0) : data.count;
            for (let contentIndex = 0; contentIndex < writeCount; contentIndex++) {
                const partIndex = lastNonEmptyIndex + 1 + contentIndex;
                setContentLoading(aiPart, partIndex, true);
                jobs.push(
                    setAiContent(aiPart, partIndex, contentIndex)
                        .catch(() => null)
                        .then(() => {
                            setContentLoading(aiPart, partIndex, false);
                        })
                );
            }
            Promise.all(jobs).then(() => {
                unlockHeadingPart(aiPart);
                unlockFilledAndOrphanSlots();
                markPartSettled(aiPart);
            });
        }
    });
}

function watchQuestionPart(): void {
    if (watchers.questionPart) {
        return;
    }
    watchers.questionPart = Firestore.createArticleOpenAIWatcher(Store.getters.article.id, 'question', (data: OpenAICompletionData) => {
        if (data.state === 'failed') {
            const questions = Store.getters.article.questions || [];
            questions.forEach((_: unknown, index: number) => setQuestionLoading(index, false));
            if (isWholeGenerate(data)) {
                unlockAllRemainingSlots(true);
            } else {
                markPartSettled('question');
            }
            return;
        }
        if (data.count === 0) {
            const questions = Store.getters.article.questions || [];
            questions.forEach((_: unknown, index: number) => setQuestionLoading(index, false));
            markPartSettled('question');
            return;
        }
        if (data.touch > 0) {
            applyQuestionValues().finally(() => {
                const questions = Store.getters.article.questions || [];
                questions.forEach((_: unknown, index: number) => setQuestionLoading(index, false));
                unlockFilledAndOrphanSlots();
                markPartSettled('question');
            });
        }
    });
}

function applyQuestionValues(): Promise<void> {
    if (Store.getters.article?.isAnalyze) {
        return Promise.resolve();
    }
    if (!clientMayApplyCompletion()) {
        return Promise.resolve();
    }
    return Firestore.getArticleOpenAIValues(Store.getters.article.id, 'question')
        .then((content) => {
            const article: Article = Store.getters.article;
            const corpus = article.isAnalyze ? pageCorpus(article as unknown as Record<string, unknown>) : '';
            content.forEach((aiQuestion) => {
                if (!clientMayApplyCompletion()) {
                    return;
                }
                if (!aiQuestion.keyword && !aiQuestion.value2 && aiQuestion.state !== 'failed') {
                    return;
                }
                if (article.isAnalyze && isOffPageCompetitorTopic(aiQuestion.keyword || aiQuestion.value1, corpus)) {
                    return;
                }
                let index = article.questions.findIndex((item) =>
                    textsMatch(item.question, aiQuestion.keyword || '')
                    || textsMatch(item.question, aiQuestion.value1 || '')
                );
                if (index === -1) {
                    if (!plainHeadingText(aiQuestion.value2)) {
                        return;
                    }
                    index = article.questions.length;
                    Store.commit('insertArticleQuestion', {
                        index,
                        aiLoading: false
                    });
                    if (aiQuestion.keyword || aiQuestion.value1) {
                        const label = savedFaqQuestion(aiQuestion, '');
                        Store.commit('setArticleQuestion', {
                            index,
                            questionKey: 'question',
                            value: article.isAnalyze
                                ? label
                                : reformatOpenAiResponseToHtml(label)
                        });
                    }
                }
                if (aiQuestion.state === 'failed') {
                    Store.commit('setArticleAiFailed', true);
                    setQuestionLoading(index, false);
                    return;
                }
                if (plainHeadingText(aiQuestion.value2)) {
                    const label = savedFaqQuestion(aiQuestion, article.questions[index]?.question);
                    Store.commit('setArticleQuestion', {
                        index,
                        questionKey: 'question',
                        value: article.isAnalyze ? label : reformatOpenAiResponseToHtml(label)
                    });
                    Store.commit('setArticleQuestion', {
                        index,
                        questionKey: 'answer',
                        value: article.isAnalyze
                            ? finalizeAnalyzedBody(
                                aiQuestion.value2,
                                corpus,
                                article.questions[index]?.answer || '',
                                collectMovableHeadings(article as unknown as Record<string, unknown>),
                                article.questions[index]?.question || aiQuestion.keyword || ''
                            )
                            : reformatOpenAiResponseToHtml(aiQuestion.value2)
                    });
                }
                setQuestionLoading(index, false);
            });
        });
}

function watchForAiMeta() {
    setMetaLoading(true);
    watchers.meta = Firestore.createArticleOpenAIWatcher(Store.getters.article.id, 'meta', (data: OpenAICompletionData) => {
        if (data.state === 'failed') {
            setMetaLoading(false);
            markPartSettled('meta');
        } else if (data.count === 0) {
            setMetaLoading(false);
            markPartSettled('meta');
        } else if (data.touch > 0) {
            setAiMeta().finally(() => {
                setMetaLoading(false);
                markPartSettled('meta');
            });
        }
    });
}

function watchForAiQuestion(index: number, question: string) {
    setQuestionLoading(index, true);
    watchers['q-' + index] = Firestore.createArticleOpenAIWatcher(Store.getters.article.id, 'question', (data: OpenAICompletionData) => {
        if (data.state === 'failed') {
            setQuestionLoading(index, false);
        } else if (data.count === 0 && !isWholeGenerate(data)) {
            setQuestionLoading(index, false);
        } else if (data.touch > 0) {
            setAiQuestion(index, question).finally(() => {
                setQuestionLoading(index, false);
            });
        }
    });
}

function setAiMeta() {
    if (Store.getters.article?.isAnalyze) {
        return Promise.resolve();
    }
    if (!clientMayApplyCompletion()) {
        return Promise.resolve();
    }
    return Firestore.getArticleOpenAIValues(Store.getters.article.id, 'meta')
        .then((content) => {
            if (content.length && content[0]) {
                if (content[0].state === 'failed') {
                    Store.commit('setArticleAiFailed', true);
                }
                for (const metaPart of ['metaTitle', 'metaDescription']) {
                    if (!Store.getters.article[metaPart].length) {
                        Store.commit('setArticleMeta', {
                            metaPart: metaPart,
                            value: metaPart === 'metaTitle' ? content[0].value1 : content[0].value2
                        });
                    }
                }
            }
        });
}

function setAiContent(aiPart: OpenAiCompletionType & ArticleContentKey, partIndex: number, contentIndex: number): Promise<void> {
    if (Store.getters.article?.isAnalyze) {
        return Promise.resolve();
    }
    if (!clientMayApplyCompletion()) {
        return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
        Firestore.getArticleOpenAIValues(Store.getters.article.id, aiPart)
            .then((content) => {
                if (!clientMayApplyCompletion()) {
                    resolve();
                    return;
                }
                const value = selectWritableCompletions(content, aiPart)[contentIndex];
                if (!value || (!value.value1 && value.state !== 'failed')) {
                    resolve();
                    return;
                }
                if (value.value1) {
                    const analyze = !!Store.getters.article?.isAnalyze;
                    const blocks = (Store.getters.article?.[aiPart] || []) as ArticlePartContent[];
                    const visibleHeading = plainHeadingText(value.value1);
                    const matched = analyze
                        ? findHeadingIndex(blocks, visibleHeading)
                        : -1;
                    for (const contentKey of ['h', 'p']) {
                        let next = contentKey === 'h' ? value.value1 : value.value2;
                        if (contentKey === 'h') {
                            if (analyze) {
                                const scraped = matched >= 0
                                    ? plainHeadingText(blocks[matched].h) || visibleHeading
                                    : headingForUrlImproveSlot(
                                        visibleHeading,
                                        value.keyword,
                                        articleHeadingOpts(aiPart as HeadingRole, value.keyword || undefined)
                                    );
                                next = scraped;
                            } else {
                                next = naturalEditorHeading(next, value.keyword, aiPart as HeadingRole);
                            }
                        }
                        if (contentKey === 'p' && analyze && matched >= 0) {
                            const articleNow = Store.getters.article;
                            const corpus = pageCorpus(articleNow as unknown as Record<string, unknown>);
                            const settled = settleSectionOrganizations(blocks[matched].p, next || '', corpus);
                            next = finalizeAnalyzedBody(
                                settled,
                                corpus,
                                blocks[matched].p,
                                collectMovableHeadings(articleNow as unknown as Record<string, unknown>),
                                blocks[matched].h
                            );
                        }
                        if (!analyze && aiPart === 'h1' && contentKey === 'h' && next && (
                            /https?:\/\//i.test(next)
                            || ((next.match(/%[0-9A-Fa-f]{2}/g) || []).length >= 3)
                        )) {
                            next = naturalEditorHeading('', value.keyword || resolveArticleMainKeyword(Store.getters.article) || '', 'h1');
                        }
                        if (analyze && contentKey === 'h' && !plainHeadingText(next)) {
                            resolve();
                            return;
                        }
                        if (aiPart === 'h1' && contentKey === 'p') {
                            next = collapseDuplicateHeadingLead(value.value1, next);
                        }
                        if (contentKey === 'p' && analyze && matched < 0) {
                            next = finalizeAnalyzedBody(next || '', pageCorpus(Store.getters.article as unknown as Record<string, unknown>));
                        }
                        Store.commit('setArticleContent', {
                            contentPart: aiPart,
                            index: partIndex,
                            contentKey: contentKey,
                            value: (contentKey === 'p' && analyze) ? (next || '') : reformatOpenAiResponseToHtml(next)
                        });
                    }
                }
                if (value.state === 'failed') {
                    // Nothing more is coming for this part - stop loading instead of waiting forever
                    Store.commit('setArticleAiFailed', true);
                }
                resolve();
            });
    });
}

function setAiQuestion(index: number, question: string): Promise<void> {
    if (!clientMayApplyCompletion()) {
        return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
        Firestore.getArticleOpenAIValues(Store.getters.article.id, 'question')
            .then((content) => {
                if (!clientMayApplyCompletion()) {
                    resolve();
                    return;
                }
                for (const aiQuestion of content) {
                    if (aiQuestion.keyword === question || /* for backward compatibility - drop in the future */ aiQuestion.value1 === question) {
                        if (aiQuestion.state === 'failed') {
                            Store.commit('setArticleAiFailed', true);
                        }
                        if (!plainHeadingText(aiQuestion.value2)) {
                            resolve();
                            return;
                        }
                        const analyze = !!Store.getters.article?.isAnalyze;
                        const corpus = analyze ? pageCorpus(Store.getters.article as unknown as Record<string, unknown>) : '';
                        for (const questionKey of ['question', 'answer']) {
                            const raw = questionKey === 'question'
                                ? savedFaqQuestion(aiQuestion, question)
                                : aiQuestion.value2;
                            Store.commit('setArticleQuestion', {
                                index: index,
                                questionKey: questionKey,
                                value: analyze && questionKey === 'answer'
                                    ? finalizeAnalyzedBody(raw, corpus)
                                    : reformatOpenAiResponseToHtml(raw)
                            });
                        }
                        resolve();
                        return;
                    }
                }
                resolve();
            });
    });
}

export function clearAiContentWatchers(): void {
    clearWholeUnlockTimer();
    wholeStatusHandled = false;
    serverArticleCommitted = false;
    pendingCompletionParts.clear();
    for (const unsubscribe of Object.values(watchers)) {
        unsubscribe();
    }
    Object.keys(watchers).forEach((key) => {
        delete watchers[key];
    });
    (Object.keys(headingAnchors) as OpenAiCompletionType[]).forEach((key) => {
        delete headingAnchors[key];
    });
}
